import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { UserSettingsRepository } from '@portal-shared/app/databases/repositories/user-settings.repository';
import { PortalUserSettingsEnum } from '@portal-shared/app/enums/portal-user-settings.enum';
import { WindowHelper } from '@portal-shared/app/helpers/window.helper';
import { Theme } from '@portal-shared/app/models/theme.model';
import { DarkTheme } from '@portal-shared/themes/dark/darkTheme';
import { DefaultTheme } from '@portal-shared/themes/default/defaultTheme';
import { THEMES } from '@portal-shared/themes/theme.config';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private static defaultLightSchemeTheme: Theme = DefaultTheme;
  private static defaultDarkSchemeTheme: Theme = DarkTheme;
  private static themeList: Theme[] = [];

  private renderer: Renderer2;
  private currentTheme$ = new BehaviorSubject<Theme | undefined>(undefined);

  public constructor(
    rendererFactory: RendererFactory2,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  /**
   * Initializer. Registers themes listed in the theme config
   * and applies the currently chosen one to the body.
   */
  public async initialize() {
    THEMES.forEach(th => ThemeService.registerTheme(th));
    await this.applyThemeToBody(await this.getTheme());
  }

  /**
   * Theme Getter. Will collect the theme in the local or session storage
   * and populate the data for the attribute currentTheme.
   * If the theme is not found in local storage, the default theme will
   * be applied.
   */
  private async getTheme() {
    if (!this.currentTheme$.value) {
      if (WindowHelper.isCypress) {
        const theme = ThemeService.defaultLightSchemeTheme;
        this.currentTheme$.next(theme);
        return theme;
      }

      const setting = await UserSettingsRepository.getUserSetting<string>(PortalUserSettingsEnum.themeKey);

      const theme = this.getThemeByName(setting);

      this.currentTheme$.next(theme);
      return theme;
    }
    return this.currentTheme$.value;
  }

  private getDefaultUserPreferredTheme() {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme:dark)').matches) {
      return ThemeService.defaultDarkSchemeTheme;
    }
    return ThemeService.defaultLightSchemeTheme;
  }

  /**
   * Theme observable Getter. Return the observable stream for
   * the theme subject.
   */
  public observeCurrentTheme() {
    return this.currentTheme$.asObservable();
  }

  /**
   * Theme Setter. Will save the theme to local storage and
   * apply it on the application.
   */
  public async setTheme(theme: Theme) {
    await this.applyThemeToBody(theme);
    this.currentTheme$.next(theme);
    await UserSettingsRepository.setUserSetting({ key: PortalUserSettingsEnum.themeKey, value: theme.name });
  }
  /**
   * Get a theme object by its name.
   * @param themeName string that represen the theme as stated in theme file.
   * @returns the theme object
   */
  public getThemeByName(themeName?: string): Theme {
    const theme = ThemeService.getThemeList().find(theme => theme.name === themeName);
    return theme ?? this.getDefaultUserPreferredTheme();
  }

  /**
   * Get the list of all themes.
   */
  public static getThemeList(): Theme[] {
    return ThemeService.themeList;
  }

  /**
   * Allows a theme file to autoregister itself in the application theme list.
   * @param theme the Theme object to register
   */
  private static registerTheme(theme: Theme) {
    ThemeService.themeList.push(theme);
  }

  /**
   * This method will apply a theme on the body element of the page by setting
   * a css class corresponding to the top-level of the theme.
   * @param theme an instance of the theme
   */
  private async applyThemeToBody(newTheme: Theme) {
    const theme = await this.getTheme();
    if (theme?.selector) {
      this.renderer.removeClass(this.document.body, theme?.selector);
    }

    if (newTheme?.selector) {
      this.renderer.addClass(this.document.body, newTheme.selector);
    }
  }
}
